import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import type { User as FirebaseUser } from 'firebase/auth';
import {
  onAuthStateChange,
  signOut as supabaseSignOut,
  signIn as supabaseSignIn,
  signUp as supabaseSignUp
} from '../lib/supabase/auth';
import { subscribeToAuthChanges } from '../lib/firebase/auth';

interface AuthContextType {
  user: SupabaseUser | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, name: string, phone: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  firebaseUser: null,
  loading: true,
  signIn: async () => {},
  signOut: async () => {},
  signUp: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to Supabase auth changes (primary auth)
    const { data: { subscription } } = onAuthStateChange((user) => {
      setUser(user);
      setLoading(false);
    });

    // Subscribe to Firebase auth changes (for accessing existing data)
    const unsubscribeFirebase = subscribeToAuthChanges((firebaseUser) => {
      setFirebaseUser(firebaseUser);
    });

    return () => {
      subscription.unsubscribe();
      unsubscribeFirebase();
    };
  }, []);

  // Clear stale Supabase session if refresh token is invalid
  useEffect(() => {
    if (!loading && user === null && firebaseUser !== null) {
      supabaseSignOut();
    }
  }, [loading, user, firebaseUser]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabaseSignIn({ email, password });
    if (error) {
      throw new Error(error.message);
    }
  };

  const signOut = async () => {
    const { error } = await supabaseSignOut();
    if (error) {
      throw new Error(error.message);
    }
  };

  const signUp = async (email: string, password: string, name: string, phone: string) => {
    const { data, error } = await supabaseSignUp({ email, password, name, phone });
    if (error) {
      throw new Error(error.message);
    }
  };

  return (
    <AuthContext.Provider value={{ user, firebaseUser, loading, signIn, signOut, signUp }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);